import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import {
  ArrowLeft,
  Image,
  Film,
  Type,
  Mic,
  Eye,
  Clock,
  MessageCircle,
  Inbox,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { getApiBaseUrl } from '@/lib/capacitor';
import { formatDate } from '@/lib/utils';

interface Post {
  _id: string;
  state: string;
  contentType: 'photo' | 'video' | 'hudText' | 'voiceNote';
  caption?: string;
  hudText?: string;
  originalFilename?: string;
  processedFilename?: string;
  voiceNoteFilename?: string;
  viewCount: number;
  user: {
    _id: string;
    name: string;
    email?: string;
  };
  createdAt: string;
  lastReadAt?: string;
}

interface ReactionCount {
  postId: string;
  reactionCount: number;
  unreadCount: number;
}

const contentTypeIcons = {
  photo: Image,
  video: Film,
  hudText: Type,
  voiceNote: Mic,
};

const stateColors: Record<string, string> = {
  uploading: 'bg-gray-500',
  uploaded: 'bg-gray-500',
  'waiting-for-processing': 'bg-purple-500',
  processing: 'bg-purple-500 animate-pulse',
  processed: 'bg-blue-500',
  unread: 'bg-amber-500',
  published: 'bg-emerald-500',
  read: 'bg-blue-500',
  archived: 'bg-gray-500',
  'processing-failed': 'bg-red-500',
  'publish-failed': 'bg-red-500',
};

interface ProcessingProgress {
  percent: number;
  frames?: number;
  fps?: number;
  time?: string;
}

export default function MyPingsPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, authenticatedFetch } = useAuth();

  // Restore scroll position when returning to this page
  useScrollRestoration('my-pings');
  const [posts, setPosts] = useState<Post[]>([]);
  const [reactionCounts, setReactionCounts] = useState<Map<string, ReactionCount>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [processingProgress, setProcessingProgress] = useState<Map<string, ProcessingProgress>>(new Map());

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/share');
      return;
    }

    if (user) {
      fetchPosts();
    }
  }, [user, authLoading, navigate]);

  // Listen for processing progress events to update UI
  useEffect(() => {
    const handleProcessingProgress = (event: Event) => {
      const customEvent = event as CustomEvent<{ postId: string; percent: number; frames?: number; fps?: number; time?: string }>;
      const { postId, percent, frames, fps, time } = customEvent.detail;
      
      setProcessingProgress(prev => {
        const next = new Map(prev);
        next.set(postId, { percent, frames, fps, time });
        return next;
      });
    };

    window.addEventListener('ping-processing-progress', handleProcessingProgress);
    return () => {
      window.removeEventListener('ping-processing-progress', handleProcessingProgress);
    };
  }, []);

  // Listen for processing complete events to refresh the list
  useEffect(() => {
    const handleProcessingComplete = (event: Event) => {
      const customEvent = event as CustomEvent<{ postId: string; success: boolean }>;
      console.log('[MyPingsPage] Processing complete event received, refreshing posts');
      
      // Clear progress for this post
      setProcessingProgress(prev => {
        const next = new Map(prev);
        next.delete(customEvent.detail.postId);
        return next;
      });
      
      fetchPosts();
    };

    window.addEventListener('ping-processing-complete', handleProcessingComplete);
    return () => {
      window.removeEventListener('ping-processing-complete', handleProcessingComplete);
    };
  }, [user]);

  const fetchPosts = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      
      // Use different endpoints based on auth type
      const postsUrl = user?.authType === 'google' 
        ? `${baseUrl}/api/posts/my`
        : `${baseUrl}/api/posts/user/${user?.id}`;
      
      const response = user?.authType === 'google'
        ? await authenticatedFetch(postsUrl)
        : await fetch(postsUrl);
      
      const data = await response.json();
      
      if (data.success) {
        setPosts(data.posts);
        
        // Fetch reaction counts for each post
        const counts = new Map<string, ReactionCount>();
        await Promise.all(
          data.posts.map(async (post: Post) => {
            try {
              const reactionsResponse = user?.authType === 'google'
                ? await authenticatedFetch(`${baseUrl}/api/reactions/post/${post._id}`)
                : await fetch(`${baseUrl}/api/reactions/post/${post._id}`);
              const reactionsData = await reactionsResponse.json();
              
              if (reactionsData.success) {
                const unreadCount = reactionsData.reactions.filter((r: { read: boolean }) => !r.read).length;
                counts.set(post._id, {
                  postId: post._id,
                  reactionCount: reactionsData.reactions.length,
                  unreadCount,
                });
              }
            } catch (error) {
              console.error('Failed to fetch reactions for post:', post._id, error);
            }
          })
        );
        setReactionCounts(counts);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMediaUrl = (filename?: string) => {
    if (!filename) return null;
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/uploads/${filename}`;
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/share">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Inbox className="w-5 h-5 text-primary" />
          <h1 className="font-display font-semibold flex-1">My Pings</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-lg mx-auto p-4 space-y-4">
        {posts.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Inbox className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">No pings yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Your sent pings will appear here
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/share')}
            >
              Send a Ping
            </Button>
          </div>
        ) : (
          posts.map((post) => {
            const Icon = contentTypeIcons[post.contentType];
            const mediaUrl = getMediaUrl(post.processedFilename || post.originalFilename);
            const counts = reactionCounts.get(post._id);
            const hasUnreadReactions = counts && counts.unreadCount > 0;
            const isProcessing = post.state === 'processing' || post.state === 'waiting-for-processing';
            const hasFailed = post.state === 'processing-failed' || post.state === 'publish-failed';
            const progress = processingProgress.get(post._id);

            return (
              <button
                key={post._id}
                onClick={() => navigate(`/my-pings/${post._id}`)}
                className="w-full text-left rounded-xl border border-border bg-card overflow-hidden hover:border-primary/50 transition-colors"
              >
                <div className="flex">
                  {/* Thumbnail */}
                  <div className="w-24 h-24 flex-shrink-0 bg-muted relative">
                    {post.contentType === 'photo' && mediaUrl && (
                      <img
                        src={mediaUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                    {post.contentType === 'video' && mediaUrl && !isProcessing && (
                      <video
                        src={mediaUrl}
                        className="w-full h-full object-cover"
                        muted
                      />
                    )}
                    {post.contentType === 'video' && isProcessing && (
                      <div className="w-full h-full flex items-center justify-center bg-purple-500/10">
                        <Film className="w-8 h-8 text-purple-400" />
                      </div>
                    )}
                    {(post.contentType === 'hudText' || post.contentType === 'voiceNote') && (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    
                    {/* Processing overlay with progress */}
                    {isProcessing && (
                      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-1">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                        {progress && (
                          <span className="text-white text-xs font-medium">
                            {Math.round(progress.percent)}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-3 min-w-0 space-y-1.5">
                    {/* State badge - enhanced for processing with progress */}
                    {isProcessing ? (
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span className="text-xs font-medium">
                          {progress 
                            ? `Processing... ${Math.round(progress.percent)}%` 
                            : 'Processing video...'}
                        </span>
                      </div>
                    ) : hasFailed ? (
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                        <div className="w-2 h-2 rounded-full bg-red-500" />
                        <span className="text-xs font-medium">
                          {post.state === 'processing-failed' ? 'Processing failed' : 'Publish failed'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            stateColors[post.state] || 'bg-muted'
                          }`}
                        />
                        <span className="text-xs text-muted-foreground capitalize">
                          {post.state.replace('-', ' ')}
                        </span>
                      </div>
                    )}

                    {/* Text preview */}
                    {(post.caption || post.hudText) && (
                      <p className="text-sm line-clamp-2 text-foreground">
                        {post.caption || post.hudText}
                      </p>
                    )}

                    {/* Meta row */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(post.createdAt)}
                      </span>
                      {!isProcessing && (
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.viewCount}
                        </span>
                      )}
                      {counts && counts.reactionCount > 0 && (
                        <span className={`flex items-center gap-1 ${hasUnreadReactions ? 'text-primary font-medium' : ''}`}>
                          <MessageCircle className={`w-3 h-3 ${hasUnreadReactions ? 'fill-primary' : ''}`} />
                          {counts.reactionCount}
                          {hasUnreadReactions && (
                            <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-primary" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </main>
    </div>
  );
}
